import CryptoJS from 'crypto-js'

import { ConnectorId, connectors } from './connectors.config'

// Secret key for encryption
if (!process.env.RMS_ENCRYPTION_KEY) {
  throw new Error('RMS_ENCRYPTION_KEY is required but not set')
}
const ENCRYPTION_KEY = process.env.RMS_ENCRYPTION_KEY

// Derive the 32-byte key using SHA-256, matching the backend
const getDerivedKey = (): CryptoJS.lib.WordArray => {
  return CryptoJS.SHA256(ENCRYPTION_KEY)
}

// Generate a random IV for each encryption operation
const getRandomIv = (): CryptoJS.lib.WordArray => {
  return CryptoJS.lib.WordArray.random(16) // 16 bytes (128 bits) random IV
}

// Encrypt data using parameters matching the backend
const encrypt = (data: string): string => {
  const key = getDerivedKey()
  const iv = getRandomIv() // Random IV for each encryption
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  // Convert IV to hex and prepend to ciphertext
  // This matches backend format of IV + ciphertext before Base64 encoding
  const ivHex = iv.toString(CryptoJS.enc.Hex)
  const ciphertextHex = CryptoJS.enc.Base64.parse(encrypted.toString()).toString(CryptoJS.enc.Hex)
  const combined = ivHex + ciphertextHex
  // Base64 encode the combined value to match backend format
  return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Hex.parse(combined))
}

// Decrypt data using parameters matching the backend
export const decrypt = (encryptedBase64: string): string => {
  const key = getDerivedKey()
  // Get binary data
  const encryptedBinary = CryptoJS.enc.Base64.parse(encryptedBase64)
  // Convert to hex for easier manipulation
  const encryptedHex = encryptedBinary.toString(CryptoJS.enc.Hex)
  // Extract IV (first 16 bytes = 32 hex chars)
  const ivHex = encryptedHex.substring(0, 32)
  const ciphertextHex = encryptedHex.substring(32)
  // Convert back to CryptoJS format
  const iv = CryptoJS.enc.Hex.parse(ivHex)
  const ciphertext = CryptoJS.enc.Hex.parse(ciphertextHex)
  const ciphertextBase64 = CryptoJS.enc.Base64.stringify(ciphertext)
  // Decrypt using the extracted IV
  const decrypted = CryptoJS.AES.decrypt(ciphertextBase64, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  return decrypted.toString(CryptoJS.enc.Utf8)
}

// Token scope type for auxiliary tokens
type TokenScope = 'main' | string

// Tokens are per tab (sessionStorage), but a sign-out or account switch happens in
// one tab. Sign-out bumps a counter shared by all tabs (localStorage); each tab
// stamps the counter it stored its tokens under and drops them once it no longer
// matches, so another tab never serves the previous account's token.
const SESSION_EPOCH_KEY = 'session_epoch' // localStorage: shared by every tab
const TAB_EPOCH_KEY = 'session_epoch_tab' // sessionStorage: this tab's stamp

const currentEpoch = (): string => {
  try {
    return localStorage.getItem(SESSION_EPOCH_KEY) ?? '0'
  } catch {
    return '0'
  }
}

const removeTabTokens = (): void => {
  const keys: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key && (key.startsWith('c_') || key.startsWith('e_'))) keys.push(key)
  }
  keys.forEach((key) => sessionStorage.removeItem(key))
  sessionStorage.removeItem(TAB_EPOCH_KEY)
}

// True when this tab's tokens were stored under the current epoch. Otherwise the
// tokens belong to a session that has since signed out: drop them.
const tabTokensCurrent = (): boolean => {
  if (sessionStorage.getItem(TAB_EPOCH_KEY) === currentEpoch()) return true
  removeTabTokens()
  return false
}

// Create a more obscure key for storage with optional scope
const getStorageKey = (source: ConnectorId, scope: TokenScope = 'main'): string => {
  const sourceHash = CryptoJS.MD5(source).toString().substring(0, 8)
  const scopeHash = scope === 'main' ? '' : `_${CryptoJS.MD5(scope).toString().substring(0, 6)}`
  return `c_${sourceHash}${scopeHash}`
}

// Create a more obscure key for expiry with optional scope
const getExpiryKey = (source: ConnectorId, scope: TokenScope = 'main'): string => {
  const sourceHash = CryptoJS.MD5(source).toString().substring(0, 8)
  const scopeHash = scope === 'main' ? '' : `_${CryptoJS.MD5(scope).toString().substring(0, 6)}`
  return `e_${sourceHash}${scopeHash}`
}

// Get a credential token with optional scope
export const getCredential = (source: ConnectorId, scope: TokenScope = 'main'): string | null => {
  if (!tabTokensCurrent()) return null
  const storageType = sessionStorage
  const encryptedToken = storageType.getItem(getStorageKey(source, scope))
  if (!encryptedToken) return null
  try {
    return decrypt(encryptedToken)
  } catch (error) {
    console.error(`Error decrypting ${scope} token for ${source}:`, error)
    storageType.removeItem(getStorageKey(source, scope))
    return null
  }
}

// Get all credentials (Retrieves encrypted data, used internally or for debugging)
export const getCredentials = (): Record<ConnectorId, string | null> => {
  const credentials: Record<ConnectorId, string | null> = {} as any
  for (const source of Object.keys(connectors) as ConnectorId[]) {
    const token = sessionStorage.getItem(getStorageKey(source)) // Gets encrypted token
    credentials[source] = token
  }
  return credentials
}

// Get expiry time for a token with optional scope
export const getTokenExpiry = (source: ConnectorId, scope: TokenScope = 'main'): number | null => {
  if (!tabTokensCurrent()) return null
  const storageType = sessionStorage
  const encryptedExpiry = storageType.getItem(getExpiryKey(source, scope))
  if (!encryptedExpiry) return null
  try {
    const decryptedExpiry = decrypt(encryptedExpiry)
    return parseInt(decryptedExpiry)
  } catch (error) {
    console.error(`Error decrypting ${scope} expiry time for ${source}:`, error)
    storageType.removeItem(getExpiryKey(source, scope))
    return null
  }
}

// Set a credential with expiry and optional scope
export const setCredential = (
  source: ConnectorId,
  token: string | null,
  expiresInSeconds?: number,
  isEncrypted = false, // Flag indicating if 'token' param is already encrypted (from backend)
  scope: TokenScope = 'main'
): void => {
  const storageType = sessionStorage
  // A write re-stamps the tab's epoch, so drop any tokens from a signed-out
  // session first; otherwise refreshing one connector would revalidate the rest.
  tabTokensCurrent()

  if (token) {
    // If token is already encrypted from backend, decrypt it first to get the raw token
    let actualToken = token
    if (isEncrypted) {
      try {
        actualToken = decrypt(token)
      } catch (error) {
        console.error(
          `CRITICAL: Error decrypting ${scope} token received from backend for ${source}:`,
          error
        )
        console.error(`Received encrypted token was: ${token}`)
        storageType.removeItem(getStorageKey(source, scope))
        storageType.removeItem(getExpiryKey(source, scope))
        return
      }
    }
    // Encrypt the actual raw token before storing
    const encryptedToken = encrypt(actualToken)
    storageType.setItem(getStorageKey(source, scope), encryptedToken)
    storageType.setItem(TAB_EPOCH_KEY, currentEpoch())
    // If expiry is provided, encrypt and store it
    if (expiresInSeconds !== undefined && expiresInSeconds !== null) {
      const expiryTime = Date.now() + expiresInSeconds * 1000
      const encryptedExpiry = encrypt(expiryTime.toString())
      storageType.setItem(getExpiryKey(source, scope), encryptedExpiry)
    } else {
      storageType.removeItem(getExpiryKey(source, scope))
    }
  } else {
    // Clear token and expiry if token is null
    storageType.removeItem(getStorageKey(source, scope))
    storageType.removeItem(getExpiryKey(source, scope))
  }
}

// Check if a token is expired with optional scope
export const isTokenExpired = (source: ConnectorId, scope: TokenScope = 'main'): boolean => {
  const expiryTime = getTokenExpiry(source, scope)
  if (!expiryTime) return true
  return Date.now() > expiryTime
}

// Clear credentials for a specific source and scope
export const clearCredentials = (source: ConnectorId, scope?: TokenScope): void => {
  if (scope) {
    // Clear specific scope
    const storageType = sessionStorage
    storageType.removeItem(getStorageKey(source, scope))
    storageType.removeItem(getExpiryKey(source, scope))
  } else {
    // Clear main tokens, and any an earlier build left in localStorage
    sessionStorage.removeItem(getStorageKey(source, 'main'))
    sessionStorage.removeItem(getExpiryKey(source, 'main'))
    localStorage.removeItem(getStorageKey(source, 'main'))
    localStorage.removeItem(getExpiryKey(source, 'main'))

    // Clear all auxiliary tokens for this source
    const sourceHash = CryptoJS.MD5(source).toString().substring(0, 8)
    const keysToRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && (key.startsWith(`c_${sourceHash}_`) || key.startsWith(`e_${sourceHash}_`))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key))
  }
}

// Clear all credentials
export const clearAllCredentials = (): void => {
  for (const source of Object.keys(connectors) as ConnectorId[]) {
    clearCredentials(source)
  }
  sessionStorage.removeItem(TAB_EPOCH_KEY)
  try {
    localStorage.setItem(SESSION_EPOCH_KEY, String(Date.now())) // invalidates the other tabs' tokens
  } catch {
    // storage unavailable — this tab is cleared either way
  }
}

// Connector tokens live in sessionStorage so they end with the tab (CASA 6.6.1);
// builds before November 2026 kept them in localStorage. Drop any that are left
// so nothing confidential outlives the session. Runs once when this module loads.
export const purgeLegacyLocalStorageCredentials = (): void => {
  try {
    for (const source of Object.keys(connectors) as ConnectorId[]) {
      localStorage.removeItem(getStorageKey(source, 'main'))
      localStorage.removeItem(getExpiryKey(source, 'main'))
    }
  } catch {
    // storage unavailable (private mode, blocked) — nothing to purge
  }
}
if (typeof window !== 'undefined') purgeLegacyLocalStorageCredentials()

// PKCE Helper Functions for OneDrive
export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  let result = ''
  for (let i = 0; i < array.length; i++) {
    result += String.fromCharCode(array[i])
  }
  return btoa(result).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  const byteArray = new Uint8Array(digest)
  let result = ''
  for (let i = 0; i < byteArray.length; i++) {
    result += String.fromCharCode(byteArray[i])
  }
  return btoa(result).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}
